import React from "react";
import { useForm } from "react-hook-form";
import { Beer, Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Amenity } from "@/types";
interface AddAmenityDialogProps {
  onAdd: (amenity: Amenity) => void;
  initialData?: Amenity;
  onCancel?: () => void;
}
const AMENITY_TYPES = ["food", "drink", "entertainment", "service"] as const;
const AddAmenityDialog: React.FC<AddAmenityDialogProps> = ({
  onAdd,
  initialData,
  onCancel
}) => {
  const {
    register,
    handleSubmit,
    setValue,
    formState: {
      errors
    },
    reset
  } = useForm<Amenity>({
    defaultValues: initialData || {
      name: "",
      description: "",
      type: "food"
    }
  });
  const onSubmit = (data: Amenity) => {
    onAdd(data);
    reset();
  };
  return <Dialog data-sentry-element="Dialog" data-sentry-component="AddAmenityDialog" data-sentry-source-file="AddAmenityDialog.tsx">
            <DialogTrigger asChild data-sentry-element="DialogTrigger" data-sentry-source-file="AddAmenityDialog.tsx">
                <Button variant="ghost" size="sm" className="flex items-center gap-2 text-sky-400 hover:bg-sky-400/10" data-sentry-element="Button" data-sentry-source-file="AddAmenityDialog.tsx">
                    <Plus className="w-4 h-4" data-sentry-element="Plus" data-sentry-source-file="AddAmenityDialog.tsx" />
                    <span>Add Amenity</span>
                </Button>
            </DialogTrigger>
            <DialogContent className="bg-slate-950/95 border-slate-700" data-sentry-element="DialogContent" data-sentry-source-file="AddAmenityDialog.tsx">
                <DialogHeader data-sentry-element="DialogHeader" data-sentry-source-file="AddAmenityDialog.tsx">
                    <DialogTitle className="text-slate-100" data-sentry-element="DialogTitle" data-sentry-source-file="AddAmenityDialog.tsx">
                        {initialData ? "Edit" : "Add"} Amenity
                    </DialogTitle>
                </DialogHeader>
                <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
                    <div>
                        <Label htmlFor="name" className="text-slate-100" data-sentry-element="Label" data-sentry-source-file="AddAmenityDialog.tsx">
                            Name
                        </Label>
                        <Input id="name" {...register("name", {
            required: "Name is required"
          })} className="bg-slate-900/50 border-slate-700 text-slate-100" placeholder="e.g., Hearty Stew, Minstrel Performance" data-sentry-element="Input" data-sentry-source-file="AddAmenityDialog.tsx" />
                        {errors.name && <p className="text-red-400 text-sm mt-1">
                                {errors.name.message}
                            </p>}
                    </div>

                    <div>
                        <Label htmlFor="type" className="text-slate-100" data-sentry-element="Label" data-sentry-source-file="AddAmenityDialog.tsx">
                            Type
                        </Label>
                        <Select defaultValue={initialData?.type || "food"} onValueChange={value => setValue("type", value as Amenity["type"])} data-sentry-element="Select" data-sentry-source-file="AddAmenityDialog.tsx">
                            <SelectTrigger className="bg-slate-900/50 border-slate-700 text-slate-100" data-sentry-element="SelectTrigger" data-sentry-source-file="AddAmenityDialog.tsx">
                                <SelectValue placeholder="Select type" data-sentry-element="SelectValue" data-sentry-source-file="AddAmenityDialog.tsx" />
                            </SelectTrigger>
                            <SelectContent className="bg-slate-950 border-slate-700" data-sentry-element="SelectContent" data-sentry-source-file="AddAmenityDialog.tsx">
                                {AMENITY_TYPES.map(type => <SelectItem key={type} value={type} className="text-slate-100 focus:bg-slate-900/50 focus:text-sky-200">
                                        {type.charAt(0).toUpperCase() + type.slice(1)}
                                    </SelectItem>)}
                            </SelectContent>
                        </Select>
                    </div>

                    <div>
                        <Label htmlFor="description" className="text-slate-100" data-sentry-element="Label" data-sentry-source-file="AddAmenityDialog.tsx">
                            Description
                        </Label>
                        <Textarea id="description" {...register("description", {
            required: "Description is required"
          })} className="bg-slate-900/50 border-slate-700 text-slate-100" placeholder="Describe the amenity..." data-sentry-element="Textarea" data-sentry-source-file="AddAmenityDialog.tsx" />
                        {errors.description && <p className="text-red-400 text-sm mt-1">
                                {errors.description.message}
                            </p>}
                    </div>

                    <div className="flex justify-end gap-2">
                        {onCancel && <Button type="button" variant="ghost" onClick={onCancel} className="text-sky-400 hover:bg-sky-400/10">
                                Cancel
                            </Button>}
                        <Button type="submit" className="bg-sky-600 hover:bg-sky-700" data-sentry-element="Button" data-sentry-source-file="AddAmenityDialog.tsx">
                            <Beer className="w-4 h-4 mr-2" data-sentry-element="Beer" data-sentry-source-file="AddAmenityDialog.tsx" />
                            {initialData ? "Update" : "Add"} Amenity
                        </Button>
                    </div>
                </form>
            </DialogContent>
        </Dialog>;
};
export default AddAmenityDialog;